import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/auth';
import { isAbort, listFabrics, type ListItem, type ListResponse } from '../api/client';
import { openCamera } from '../capture';
import { loginHref } from '../components/AccessDenied';
import ErrorState from '../components/ErrorState';
import { Icon, type IconName } from '../components/Icon';
import FabricCard from '../components/FabricCard';
import Illustration from '../components/Illustration';
import Swatch from '../components/Swatch';
import type { DetailState } from '../nav';
import { NearScope, useNear } from '../near';
import { COLOR_KO } from '../vocab';

/** §1.1 지원 질의 유형 예시 — 사양서에 명시된 문장을 그대로 쓴다.
 *  아이콘은 질의 유형(색·패턴 / 분위기 / 용도 / 품번)을 알려 준다 (R1-3). */
const EXAMPLES: Array<{ q: string; icon: IconName }> = [
  { q: '따뜻한 느낌의 노란색 작은 꽃무늬 원단', icon: 'palette' },
  { q: '고급스러운 베이지 원단', icon: 'palette' },
  { q: '고급 호텔 로비 같은 분위기', icon: 'sparkle' },
  { q: '여름에 어울리는 시원한 느낌', icon: 'sparkle' },
  { q: '포근한 느낌의 소파용 원단', icon: 'fabric' },
  { q: '빈티지하면서 너무 화려하지 않은 패턴', icon: 'pattern-repeat' },
  { q: 'KIN-20343', icon: 'swatch' },
];

/**
 * 홈은 한 번에 이만큼 받아 나눠 그린다(컨셉 §7.7): 맨 앞 3건 = 「새로 들어온 원단」 가로 띠, 그다음 「현재 열 수 × 2」 = 최근 등록 격자,
 * 모자이크는 그 뒤에서 색이 겹치지 않게 고른다(위 둘과 같은 원단을 되풀이하지 않게). 1440 기둥 7열 × 2 = 14 까지 덮는다.
 */
const HOME_FETCH = 40;
const BANNERS = 3;
const MOSAIC = 12;
/** 모자이크가 먼저 비켜 갈 앞쪽(띠 3 + 격자 최대 7열 × 2) */
const MOSAIC_AVOID = BANNERS + 14;
/** 색 계열 줄 수 · 줄마다 견본 수(컨셉 §7.7 「견본 6~8장」) */
const FAMILIES = 6;
const PER_FAMILY = 8;
/** 줄마다 몇 장 더 받아 모자이크와 같은 원단을 뒤로 미룬다(첫 화면에 같은 견본이 두 번 보이지 않게) */
const FAMILY_FETCH = PER_FAMILY + 4;
/**
 * 모자이크가 이만큼만 남으면(그 사진들은 이미 연결을 잡고 받는 중) 나머지 사진을 풀어 준다 — 다 끝날 때까지 기다리면 연결이 노는 꼬리가 생긴다.
 * HTTP/1.1 한 곳 동시 연결 6 과 같은 수(작은 견본으로 바이트가 줄어 느린 망에서는 요청 지연이 시간을 정한다, 15회차 M33 실측)
 */
const HERO_TAIL = 6;
/**
 * 「모자이크 먼저」 의 최대 대기 — 홈이 열린 때부터 센다(Codex 9차 ①: 예전에는 주 목록이 온 뒤에야 10초를 셌다 — 주 목록 요청이 멈추면
 * 따로 받은 색 계열 사진이 끝없이 기다렸다). 느린 망(Fast 3G)의 보통 경우는 열린 뒤 약 2초(1280 2.1 · 768 2.3 · 375 2.0 s 실측)에 풀려 이 상한에 닿지 않는다
 */
const HOLD_MAX_MS = 10_000;
const DESKTOP = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 721px) and (pointer: fine)').matches;
/** 홈에서 들어간 상세의 PREV / NEXT 는 전체 목록(최근 등록 순) 기준, 「목록으로」 는 없다(HOME 이 그 일) */
const HOME_STATE: DetailState = { list: { sort: 'recent' }, from: '/' };

/** 격자의 실제 열 수 — auto-fill 은 비어 있어도 트랙을 만든다 */
function useGridColumns(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(6);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const n = getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length;
      if (n > 0) setCols(n);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, cols];
}

/** 접근 이름 = 이름 + 품번 (컨셉 §7.7) */
const nameOf = (it: { fabric_name: string; part_number?: string | null }) =>
  `${it.fabric_name}${it.part_number ? ` (${it.part_number})` : ''}`;

/**
 * 모자이크 견본 고르기 — 서로 다른 주 색상 · 다른 이름(같은 도안의 다른 배색을 되풀이하지 않게)을 먼저,
 * 그다음 다른 이름, 그래도 모자라면 남은 것. 앞쪽(띠 · 격자)과 겹치지 않는 것을 먼저 본다.
 */
function pickMosaic(items: ListItem[], n: number): ListItem[] {
  const order = [...items.slice(MOSAIC_AVOID), ...items.slice(0, MOSAIC_AVOID)];
  const out: ListItem[] = [];
  const colors = new Set<string>();
  const names = new Set<string>();
  const take = (ok: (it: ListItem) => boolean) => {
    for (const it of order) {
      if (out.length >= n) return;
      if (out.includes(it) || !ok(it)) continue;
      out.push(it);
      if (it.summary?.dominant_color) colors.add(it.summary.dominant_color);
      names.add(it.fabric_name);
    }
  };
  take((it) => !!it.summary?.dominant_color && !colors.has(it.summary.dominant_color) && !names.has(it.fabric_name));
  take((it) => !names.has(it.fabric_name));
  take(() => true);
  return out;
}

/** 「n월 n일 등록」 — 서버 등록 시각을 이 기기 날짜로 */
function regDay(s?: string | null): string | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : `${d.getMonth() + 1}월 ${d.getDate()}일 등록`;
}

/**
 * 「색 계열로 둘러보기」 (컨셉 §7.7) — 색 이름 · 건수 · 견본 8장.
 * 15회차 M33 · Codex 8차 B6: 예전에는 구역 하나를 관찰해 6줄 48장을 한꺼번에 받았다(느린 망에서 첫 화면 사진과 경쟁).
 * 이제 **줄마다** 화면 가까이 올 때 그 줄의 견본 목록을 받고, 견본 사진도 칸마다 가까울 때 넣는다(near.ts).
 * 색 이름 · 건수는 목록 facets.color(카드 · 색 필터와 같은 색 근거) 한 번 — 구역이 가까울 때. 색 이름을 누르면 전체 원단의 그 색 필터로.
 * 자리는 처음부터 줄 수 × 견본 수만큼 잡아 두어(골격) 견본이 와도 아래가 밀리지 않는다.
 */
type Fam = { value: string; count: number };

/**
 * shown — 이미 모자이크에 있는 원단. 그 원단은 줄 뒤로 미루고(모자라면 채운다), 쓸 때는 모자이크와 **같은 사진 주소**를 쓴다 —
 * 서명이 다른 같은 사진을 한 번 더 받지 않게(실측 1280 Fast 3G: 같은 견본 2장 105KB 를 다시 받았다)
 */
function FamilyRow({ f, shown }: { f: Fam | null; shown: Map<string, ListItem> }) {
  const [ref, near] = useNear<HTMLLIElement>();
  const [items, setItems] = useState<ListItem[] | null>(null);
  const ko = f ? COLOR_KO[f.value] : undefined;
  useEffect(() => {
    if (!near || !f) return;
    const ctl = new AbortController();
    listFabrics({ sort: 'recent', page: 1, size: FAMILY_FETCH, color: f.value }, ctl.signal)
      .then((x) => { if (!ctl.signal.aborted) setItems(x.results); })
      .catch((e) => { if (!isAbort(e) && !ctl.signal.aborted) setItems([]); });
    return () => ctl.abort();
  }, [near, f?.value]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <li ref={ref} className="fam">
      <div className="fam-head">
        {f && ko ? (
          <Link className="fam-name" to={`/browse?color=${encodeURIComponent(f.value)}`}
                aria-label={`${ko} 계열 ${f.count.toLocaleString()}건 모두 보기`}>
            {ko}<Icon name="chevron-right" size={16} />
          </Link>
        ) : <span className="skel-line w-60" aria-hidden="true" />}
        {f && ko && <span className="fam-n">{f.count.toLocaleString()}건</span>}
      </div>
      <ul className="fam-strip" aria-label={ko ? `${ko} 계열 견본` : undefined} aria-hidden={ko ? undefined : true}
          aria-busy={(ko && !items) || undefined}>
        {items
          ? [...items.filter((x) => !shown.has(x.fabric_id)), ...items.filter((x) => shown.has(x.fabric_id))].slice(0, PER_FAMILY)
            .map((x) => { const m = shown.get(x.fabric_id); return m ? { ...x, swatch_url: m.swatch_url, swatch_sm_url: m.swatch_sm_url, thumb_url: m.thumb_url } : x; })
            .map((it) => (
            <li key={it.fabric_id}>
              <Link className="fam-tile sw-frame" to={`/fabric/${it.fabric_id}`} state={HOME_STATE} aria-label={nameOf(it)} title={nameOf(it)}>
                <Swatch fabricId={it.fabric_id} url={it.swatch_url} small={it.swatch_sm_url} fallback={it.thumb_url} className="swatch" alt="" iconSize={20} defer priority="low"
                        sizes="(max-width: 720px) 88px, (max-width: 1023px) 13vw, 140px" />
              </Link>
            </li>
          ))
          : Array.from({ length: PER_FAMILY }, (_, i) => <li key={i}><span className="fam-tile skel-box" /></li>)}
      </ul>
    </li>
  );
}

function ColorFamilies({ shown }: { shown: Map<string, ListItem> }) {
  const [box, near] = useNear<HTMLElement>();
  const [fams, setFams] = useState<Fam[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!near) return;
    const ctl = new AbortController();
    setError(null);
    listFabrics({ sort: 'recent', page: 1, size: 1, facets: true }, ctl.signal).then((r) => {
      if (ctl.signal.aborted) return;
      setFams((r.facets?.color ?? []).filter((c) => c.count > 0 && COLOR_KO[c.value]).slice(0, FAMILIES));
    }).catch((e) => { if (!isAbort(e) && !ctl.signal.aborted) setError(e); });
    return () => ctl.abort();
  }, [near, retry]);

  const authErr = error instanceof ApiError && error.isAuth;
  // 색 근거가 있는 원단이 하나도 없으면(모의 · 새 서버) 줄 자체를 두지 않는다
  if (fams && fams.length === 0) return null;

  return (
    <section className="families" ref={box} aria-labelledby="fam-title">
      <div className="page offset">
        <header className="home-head">
          <h2 id="fam-title">색 계열로 둘러보기</h2>
          <span className="home-sub">측정 색 기준 · 색 이름을 누르면 그 색 원단만 모아 봅니다</span>
          <Link className="recent-all" to="/browse">모든 색 보기 <Icon name="chevron-right" size={16} /></Link>
        </header>
        {error != null ? (
          authErr ? (
            <p className="home-note"><Icon name="lock" size={16} /> 로그인하면 색 계열별 원단 견본을 볼 수 있습니다.</p>
          ) : <ErrorState error={error} onRetry={() => setRetry((n) => n + 1)} compact />
        ) : (
          <ul className="fam-list" aria-busy={!fams || undefined}>
            {fams
              ? fams.map((f) => <FamilyRow key={f.value} f={f} shown={shown} />)
              : Array.from({ length: FAMILIES }, (_, i) => <FamilyRow key={`skel-${i}`} f={null} shown={shown} />)}
          </ul>
        )}
        {!fams && error == null && <span className="sr-only" role="status">색 계열을 불러오는 중…</span>}
      </div>
    </section>
  );
}

export default function Home() {
  const [q, setQ] = useState('');
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [retry, setRetry] = useState(0);
  const nav = useNavigate();
  const [gridRef, cols] = useGridColumns();
  /**
   * 첫 화면 모자이크(보이는 칸)가 다 왔는가 — 그때까지 홈의 나머지 사진(색 계열 · 띠 · 격자)은 가까워도 기다린다(near.ts hold, 15회차 M33).
   * 모자이크가 없거나(오류 · 빈 목록) 홈이 열리고 10초가 지나면(주 목록 응답과 무관) 푼다 — 빠른 망에서는 수백 ms 안에 풀린다
   */
  const mosaicRef = useRef<HTMLElement>(null);
  const [heroDone, setHeroDone] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    setError(null);
    listFabrics({ page: 1, size: HOME_FETCH, sort: 'recent' }, ctl.signal)
      .then((r) => { if (!ctl.signal.aborted) setData(r); })
      .catch((e) => { if (!isAbort(e) && !ctl.signal.aborted) setError(e); });
    return () => ctl.abort();
  }, [retry]);

  // 최대 대기는 목록 응답과 따로 — 주 목록이 멈추거나 늦어도 이 시각에 기다리던 사진을 모두 푼다(Codex 9차 ①)
  useEffect(() => {
    const t = window.setTimeout(() => setHeroDone(true), HOLD_MAX_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (heroDone) return;
    if (error != null) { setHeroDone(true); return; }
    if (!data) return;
    const el = mosaicRef.current;
    const check = () => {
      // 보이는 모자이크 칸만(휴대폰에서 숨는 9~12번째는 받지 않으므로 세지 않는다)
      const imgs = el ? Array.from(el.querySelectorAll('img')).filter((i) => i.getClientRects().length > 0) : [];
      if (imgs.filter((i) => !i.complete).length <= HERO_TAIL) setHeroDone(true);
    };
    const raf = requestAnimationFrame(check);
    el?.addEventListener('load', check, true);
    el?.addEventListener('error', check, true);
    return () => { cancelAnimationFrame(raf); el?.removeEventListener('load', check, true); el?.removeEventListener('error', check, true); };
  }, [data, error, heroDone]);

  const go = (text: string) => {
    const t = text.trim();
    if (t) nav(`/search?q=${encodeURIComponent(t)}`);
  };

  const items = data?.results ?? [];
  // 컨셉 §7.5 · §7.7: 홈은 미리보기 — 가로 띠 3건 + 격자 정확히 2줄(현재 열 수 × 2). 전체는 「전체 원단」에서
  const banners = items.slice(0, BANNERS);
  const shown = items.slice(BANNERS, BANNERS + cols * 2);
  const mosaic = useMemo(() => pickMosaic(data?.results ?? [], MOSAIC), [data]);
  const mosaicById = useMemo(() => new Map(mosaic.map((it) => [it.fabric_id, it])), [mosaic]);
  const authErr = error instanceof ApiError && error.isAuth;
  const loading = !data && error == null;
  const empty = !!data && data.total === 0;

  return (
    // 15회차 M33: 홈 한 화면이 「화면 가까이」 관찰자 하나를 쓴다 — 첫 진입은 화면 안만, 굴리면 앞뒤 1화면까지(near.ts)
    <NearScope hold={!heroDone}>
    <div className="home">
      {/* R1-8 · 컨셉 §7.7: 배경은 화면 끝까지, 내용은 본문 기둥 안 레일 뒤 시작선에서. 1024 이상은 왼쪽 검색 · 오른쪽 견본 모자이크 */}
      <section className="hero">
        <div className="page offset hero-in">
        <div className="hero-text">
        <h1>어떤 원단을 찾으시나요?</h1>
        <p>느낌 · 색상 · 패턴 · 용도를 자유롭게 적어보세요. 정확한 품번을 몰라도 됩니다.</p>

        {/* R1-6 · R1-8: 히어로 검색은 화면의 주 행동 — 시작선에서, 높이 52 · 아이콘 20.
            「사진으로 검색」은 같은 줄 오른쪽(좁으면 아래 줄) */}
        <div className="hero-actions">
          <form className="searchbox" role="search" onSubmit={(e) => { e.preventDefault(); go(q); }}>
            <div className="searchbox-field">
              <Icon name="search" size={20} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="예: 따뜻한 느낌의 노란색 작은 꽃무늬 원단"
                aria-label="검색어"
                enterKeyHint="search"
                // 데스크톱에서만 바로 입력 — 휴대폰에서 자판이 먼저 올라오면 카메라 버튼을 가린다
                autoFocus={DESKTOP}
              />
            </div>
            <button type="submit" className="btn primary lg"><Icon name="search" size={20} /> 검색</button>
          </form>
          {/* 컨셉 §7.6: 휴대폰은 원단을 손에 들고 선 사람 — 검색창 바로 아래 가장 큰 주 버튼, 누르면 곧바로 카메라.
              카메라 입력은 앱 틀에 늘 붙어 있어(capture.ts) 이 클릭 안에서 열리고, 화면은 사진 검색으로 간다 */}
          <button type="button" className="btn primary xl camera-cta"
                  onClick={() => { openCamera(); nav('/image-search', { state: { camera: true } }); }}>
            <Icon name="camera" size={24} /> 카메라로 찍어서 찾기
          </button>
          <button type="button" className="btn lg photo-btn" onClick={() => nav('/image-search')}>
            <Icon name="image-search" size={20} /> 사진으로 검색
          </button>
        </div>

        <div className="examples" role="group" aria-label="검색 예시">
          {EXAMPLES.map((ex) => (
            <button key={ex.q} type="button" className="chip-btn" onClick={() => { setQ(ex.q); go(ex.q); }}>
              <Icon name={ex.icon} size={16} />{ex.q}
            </button>
          ))}
        </div>
        </div>

        {/* 컨셉 §7.7: 견본 모자이크 — 색이 겹치지 않게 고른 12장(휴대폰 8장), 간격 4px. 타일마다 상세 링크, 접근 이름 = 이름 + 품번.
            첫 화면이라 바로, 먼저 받는다(앞 8장 eager · 12장 모두 fetchpriority high — 15회차 M33). 휴대폰에서 숨는 9~12번째는 lazy 라 받지 않는다.
            자리는 처음부터 정사각 칸으로 잡혀 사진이 와도 밀리지 않는다 */}
        <section className="mosaic" ref={mosaicRef} aria-label="등록된 원단 견본" aria-busy={loading || undefined}>
          {loading || (mosaic.length > 0 && !error) ? (
            <ul className="mosaic-grid">
              {loading
                ? Array.from({ length: MOSAIC }, (_, i) => <li key={i}><span className="mosaic-tile skel-box" aria-hidden="true" /></li>)
                : mosaic.map((it, i) => (
                  <li key={it.fabric_id}>
                    <Link className="mosaic-tile sw-frame" to={`/fabric/${it.fabric_id}`} state={HOME_STATE} aria-label={nameOf(it)} title={nameOf(it)}>
                      <Swatch fabricId={it.fabric_id} url={it.swatch_url} small={it.swatch_sm_url} fallback={it.thumb_url} className="swatch" alt="" eager={i < 8} priority="high"
                              sizes="(max-width: 720px) 25vw, (max-width: 1023px) 17vw, 150px" />
                    </Link>
                  </li>
                ))}
            </ul>
          ) : (
            // 견본을 못 받았거나(로그인이 필요한 서버 · 오류) 아직 원단이 없다 — 같은 자리에 일러스트(아래 줄이 안내 문장을 맡는다)
            <div className="mosaic-empty"><Illustration name={authErr ? 'login-needed' : 'empty-recent'} /></div>
          )}
        </section>
        </div>
      </section>

      <ColorFamilies shown={mosaicById} />

      <section className="recent" aria-labelledby="recent-title">
        <div className="page offset">
        <header className="recent-head home-head">
          <h2 id="recent-title">새로 들어온 원단</h2>
          {data && data.total > 0 && (
            <span className="recent-sum">
              최근 등록 순 · 전체 {data.total.toLocaleString()}건 · 오늘 {data.today.toLocaleString()}건
            </span>
          )}
          <Link className="recent-all" to="/browse">
            전체 보기 <Icon name="chevron-right" size={16} />
          </Link>
        </header>

        {error != null && authErr && (
          <div className="state empty compact" role="status">
            <b>로그인하면 등록된 원단을 볼 수 있습니다</b>
            <p>이 서버는 원단 목록을 승인된 계정에만 보여 줍니다.</p>
            <Link className="btn sm primary" to={loginHref('/')}><Icon name="login" size={16} /> 로그인</Link>
          </div>
        )}
        {error != null && !authErr && <ErrorState error={error} onRetry={() => setRetry((n) => n + 1)} compact />}
        {empty && (
          <div className="state empty">
            <Illustration name="empty-recent" />
            <b>아직 등록된 원단이 없습니다.</b>
            <p>등록 스테이션에서 촬영·업로드한 원단이 분석을 마치면 여기에 보입니다.</p>
          </div>
        )}

        {/* 컨셉 §7.7: 가로 띠(960×400) 큰 사진 줄 — 가장 최근 3건. 휴대폰은 가로로 밀어 본다. 글자는 사진 밖(아래).
            첫 화면 밖이라 화면 가까이 올 때 사진을 넣는다(near.ts — 15회차 M33) */}
        {(loading || banners.length > 0) && (
          <ul className="arrivals" aria-busy={loading || undefined} aria-label="가장 최근에 등록된 원단">
            {loading
              ? Array.from({ length: BANNERS }, (_, i) => (
                <li key={i} aria-hidden="true"><span className="arrival"><span className="arrival-img skel-box" /><span className="skel-line w-60" /><span className="skel-line w-40" /></span></li>
              ))
              : banners.map((it) => (
                <li key={it.fabric_id}>
                  <Link className="arrival" to={`/fabric/${it.fabric_id}`} state={HOME_STATE}>
                    <span className="arrival-img sw-frame">
                      <Swatch fabricId={it.fabric_id} url={it.banner_url} fallback={[it.swatch_url, it.thumb_url]} className="swatch" alt="" defer priority="low" />
                    </span>
                    <span className="arrival-name">{it.fabric_name}</span>
                    <span className="arrival-meta">
                      {it.part_number && <code>{it.part_number}</code>}
                      {regDay(it.registered_at) && <span>{regDay(it.registered_at)}</span>}
                    </span>
                  </Link>
                </li>
              ))}
          </ul>
        )}

        {/* R1-8 · R1-9: 작은 카드 자동 배열(격자 고정) 정확히 2줄. 상세의 PREV / NEXT 는 전체 목록(최근 등록 순) 기준 — 서버 neighbors */}
        <div className="grid" ref={gridRef} aria-busy={loading}>
          {loading && Array.from({ length: cols * 2 }, (_, i) => (
            <div key={i} className="card skel" aria-hidden="true"><div className="card-thumb" /><div className="card-body"><span /><span /></div></div>
          ))}
          {shown.map((s) => (
            <FabricCard key={s.fabric_id} item={s} showScore={false} list={{ sort: 'recent' }} defer />
          ))}
        </div>
        {loading && <span className="sr-only" role="status">최근 등록 원단을 불러오는 중…</span>}
        </div>
      </section>
    </div>
    </NearScope>
  );
}
